import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import useTitle from "../../hooks/useTitle";
import useIsMobile from "../../hooks/useIsMobile";
import PulseNegocio from "./PulseNegocio";
import PulsoTaller from "./PulsoTaller";
import PulsoContable from "./PulsoContable";

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmt = dateStr => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "2-digit" });
};

const fmtMoney = val => {
  if (!val && val !== 0) return "—";
  return `$${Number(val).toLocaleString("es-CL")}`;
};

// ─── colores por estado ───────────────────────────────────────────────────────

const WO_STATUS = {
  pending:   { label: "Sin tomar",  bg: "#222",    color: "#aaa",    border: "#3a3a3a" },
  approved:  { label: "Aprobada",   bg: "#0d1a2d", color: "#3498db", border: "#0d2a4a" },
  working:   { label: "En proceso", bg: "#0d1a2d", color: "#3498db", border: "#0d2a4a" },
  inProcess: { label: "En proceso", bg: "#0d1a2d", color: "#3498db", border: "#0d2a4a" },
  ready:     { label: "Terminada",  bg: "#2d2000", color: "#f39c12", border: "#4a3500" },
  done:      { label: "Terminada",  bg: "#2d2000", color: "#f39c12", border: "#4a3500" },
  delivered: { label: "Entregada",  bg: "#0d2a15", color: "#2ecc71", border: "#0d4020" },
  invoiced:  { label: "Facturada",  bg: "#0d2a15", color: "#2ecc71", border: "#0d4020" },
};

const Badge = ({ map, status }) => {
  const s = map[status] || { label: status || "—", bg: "#222", color: "#aaa", border: "#3a3a3a" };
  return (
    <span style={{
      display: "inline-block", fontSize: 10, fontWeight: 700,
      padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap",
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      flexShrink: 0,
    }}>{s.label}</span>
  );
};

// ─── tarjeta métrica ──────────────────────────────────────────────────────────

const METRIC_COLORS = {
  blue:   "#3498db",
  amber:  "#f39c12",
  green:  "#2ecc71",
  purple: "#a78bfa",
  red:    "#e74c3c",
};

// F5.8b — estados terminales del lifecycle (usado por métricas Y filtros).
const TERMINAL_STATUSES = ["delivered", "facturado", "cancelled", "invoiced"]; // invoiced legacy

// Auditoría UX 28-ago — las 4 MetricCards filtran el Registro general.
// Cada clave agrupa los estados reales que la métrica cuenta.
const METRIC_FILTERS = {
  activas:    wo => !TERMINAL_STATUSES.includes(wo.status),
  inProcess:  wo => ["working", "inProcess"].includes(wo.status),
  done:       wo => ["ready", "done"].includes(wo.status),
  facturadas: wo => ["facturado", "invoiced"].includes(wo.status),
};

// Estilo estándar (tarjeta #232323/12, label 10/700 #8a8a8a, número tabular).
// Cliqueable: filtra el Registro general; click de nuevo = quitar filtro.
const MetricCard = ({ label, value, sub, color, style, onClick, active, loading, title }) => {
  const c = METRIC_COLORS[color] || "#fff";
  return (
    <div
      onClick={onClick}
      title={title || (onClick ? "Filtrar el registro general" : undefined)}
      className="t-02 tap-card"
      style={{
        background: active ? `${c}14` : "#232323",
        border: `1px solid ${active ? c : "#2e2e2e"}`,
        borderRadius: 12,
        padding: "12px 14px", flex: "1 1 0", minWidth: 0,
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >
      <div style={{
        fontSize: 10, fontWeight: 700, color: active ? c : "#8a8a8a", marginBottom: 4,
        textTransform: "uppercase", letterSpacing: ".05em",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
      }}>
        <span>{label}</span>
        {active && <span style={{ fontSize: 9, flexShrink: 0 }}>✕ quitar</span>}
      </div>
      {loading ? (
        <div style={{ height: 28, width: "50%", background: "#2e2e2e", borderRadius: 6, opacity: 0.6 }} />
      ) : (
        <div style={{ fontSize: 26, fontWeight: 800, color: c, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
          {value == null ? "—  sin datos" : value}
        </div>
      )}
      {sub && <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 2 }}>{sub}</div>}
    </div>
  );
};

// Pills de origen (columna C/S OC) — ahora cliqueables: filtran por origen.
const ORIGIN_PILL = {
  OC:     { label: "CON",    bg: "#0d1a2d", color: "#3498db", border: "#0d2a4a" },
  sinOC:  { label: "SIN",    bg: "#1e1535", color: "#a78bfa", border: "#3a2570" },
  verbal: { label: "verbal", bg: "#2d2000", color: "#f39c12", border: "#4a3500" },
};
const originKey = wo => (wo.origin === "OC" ? "OC" : wo.origin === "sinOC" ? "sinOC" : "verbal");

// ─── estilos compartidos ──────────────────────────────────────────────────────

const S = {
  sectionTitle: {
    fontSize: 10, fontWeight: 700, color: "#999",
    textTransform: "uppercase", letterSpacing: ".07em",
    margin: "14px 0 8px", textAlign: "left",
  },
  tableWrap: {
    border: "1px solid #2e2e2e", borderRadius: 8,
    overflow: "hidden", marginBottom: 18, background: "#242424",
  },
  th: {
    background: "#1a1a1a", padding: "7px 10px", textAlign: "left",
    fontSize: 10, fontWeight: 700, color: "#999",
    borderBottom: "1px solid #2e2e2e", textTransform: "uppercase",
    letterSpacing: ".04em",
  },
  td: {
    padding: "7px 10px", color: "#bbb",
    borderBottom: "1px solid #252525",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  card: {
    background: "#242424", border: "1px solid #2e2e2e",
    borderRadius: 8, padding: "12px 14px", flex: 1, minWidth: 0,
  },
  cardHeader: {
    fontSize: 10, fontWeight: 700, color: "#cbd5e1",
    textTransform: "uppercase", letterSpacing: ".05em",
    marginBottom: 9, paddingBottom: 7, borderBottom: "1px solid #2e2e2e",
    textAlign: "left",
  },
  row: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "6px 0", borderBottom: "1px solid #252525",
    fontSize: 12, cursor: "pointer",
  },
};

// ─── componente principal ─────────────────────────────────────────────────────

const Dashboard = () => {
  useTitle("Dashboard");
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const auth = useContext(AuthContext); // saludo con nombre en la vista móvil

  // El VENDEDOR puro no vive en el registro del taller: su casa es Cotizaciones
  // (28-ago, pedido Maxi al probar "Ver como Ventas"). Admin/operator/accountant
  // siguen acá; el redirect también aplica al lente "Ver como".
  // Roles EFECTIVOS (con el lente "Ver como" ya aplicado por AuthContext).
  const roles = auth?.user?.roles || [];
  const puedeVerPulsoContable = auth?.can?.("ver_pulso_contable") || false;
  const puedeVerTaller = auth?.can?.("ver_pulso_taller") || false;
  const puedeVerCuadrilla = auth?.can?.("ver_cuadrilla") || false;
  // Contable "puro" (sin admin/operator): no ve Bodega/Trabajos en accesos.
  const esContablePuro = roles.includes("accountant") && !roles.some(x => ["admin", "operator"].includes(x));
  const esSoloVendedor = (() => {
    const r = roles;
    return r.includes("seller") && !r.some(x => ["admin", "operator", "accountant"].includes(x));
  })();
  useEffect(() => {
    if (esSoloVendedor) navigate("/quotations", { replace: true });
  }, [esSoloVendedor, navigate]);

  const [loading, setLoading]       = useState(true);
  const [workOrders, setWorkOrders] = useState([]);
  const [regFilter,       setRegFilter]       = useState("");
  const [regStatusFilter, setRegStatusFilter] = useState("");
  // Auditoría UX 28-ago — filtro por origen (pills CON/SIN/verbal cliqueables)
  const [regOriginFilter, setRegOriginFilter] = useState("");
  // Rediseño móvil 26-ago (pedido Maxi): el dashboard del teléfono parte por
  // LA PLATA — usa el mismo /produccion/summary de la vista Producción.
  // Si el rol no tiene acceso (403), las tarjetas de dinero simplemente no van.
  const [resumen, setResumen] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const woRes = await backend.get("workOrder");
        setWorkOrders(woRes.data || []);
      } catch (err) {
        console.error("Dashboard load error:", err);
      }
      setLoading(false);
      try {
        const s = await backend.get("produccion/summary");
        setResumen(s.data);
      } catch { setResumen(null); }
    };
    load();
  }, []);

  // F5.8b — métricas alineadas al lifecycle real:
  //   Activas: cotizado + working + ready (todo lo NO terminal)
  //   Cerradas: delivered + facturado + cancelled
  //   Total queda como contador histórico, separado del operativo.
  const activas   = workOrders.filter(w => !TERMINAL_STATUSES.includes(w.status)).length;
  const cotizadas = workOrders.filter(w => w.status === "cotizado").length;
  const inProcess = workOrders.filter(w => ["working", "inProcess"].includes(w.status)).length;
  const done      = workOrders.filter(w => ["ready", "done"].includes(w.status)).length;
  const facturadas = workOrders.filter(w => ["facturado", "invoiced"].includes(w.status)).length;
  const entregadas = workOrders.filter(w => w.status === "delivered").length;
  const total      = workOrders.length;

  const recentWO = [...workOrders]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 10);

  // registro general — todos los trabajos filtrados
  const regFiltered = [...workOrders]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .filter(wo => {
      const q = regFilter.toLowerCase();
      const matchText = !q ||
        String(wo.number || "").includes(q) ||
        (wo.name  || "").toLowerCase().includes(q) ||
        (wo.client?.name || "").toLowerCase().includes(q) ||
        (wo.clientOC || "").toLowerCase().includes(q);
      // regStatusFilter acepta claves de MetricCard (grupos de estados) o un
      // estado individual del select.
      const matchStatus = !regStatusFilter || (
        METRIC_FILTERS[regStatusFilter]
          ? METRIC_FILTERS[regStatusFilter](wo)
          : wo.status === regStatusFilter
      );
      const matchOrigin = !regOriginFilter || originKey(wo) === regOriginFilter;
      return matchText && matchStatus && matchOrigin;
    });

  // Saludo con nombre (AuthContext) — compartido por móvil y desktop (28-ago).
  const nombre = (auth?.user?.firstName || "").trim();

  // ── DASHBOARD MÓVIL (26-ago, rediseño pedido por Maxi) ─────────────────────
  // App 100% personal en el teléfono: primero LA PLATA, después el taller,
  // después accesos de un toque. Cada tarjeta navega a su módulo.
  if (isMobile) {
    const fm  = resumen?.facturado_mes;
    const oca = resumen?.ocs_activas;
    const reg = resumen?.por_regularizar;
    const mesNombre = new Date().toLocaleString("es-CL", { month: "long" });
    // Lenguaje móvil 26-ago: tarjeta tocable radio 12, etiqueta 10/700 gris,
    // número 26/800 tabular, chevron alineado con flex (no float).
    const CardNav = ({ icon, titulo, valor, sub, color, to, urgente }) => (
      <div
        onClick={() => to && navigate(to)}
        className="t-02 tap-card"
        style={{
          background: urgente ? "#241416" : "#242424",
          border: `1px solid ${urgente ? "#5a2020" : "#2e2e2e"}`,
          borderRadius: 12, padding: "14px 14px", cursor: "pointer", minHeight: 64,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".06em" }}>
            {icon} {titulo}
          </span>
          <span style={{ color: "#555", fontSize: 14, lineHeight: 1 }}>›</span>
        </div>
        <div style={{ fontSize: valor == null ? 14 : 26, fontWeight: 800, color: valor == null ? "#8a8a8a" : color, lineHeight: 1.1, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums" }}>{valor == null ? "—  sin datos" : valor}</div>
        {sub && <div style={{ fontSize: 11, color: "#8a8a8a", marginTop: 4 }}>{sub}</div>}
      </div>
    );
    const Acceso = ({ icon, label, to }) => (
      <button type="button" onClick={() => navigate(to)} className="t-02 tap-card" style={{
        flex: 1, minWidth: 100, minHeight: 60, borderRadius: 12, border: "1px solid #2e2e2e",
        background: "#242424", color: "#ddd", fontSize: 12, fontWeight: 700, cursor: "pointer",
      }}>
        <div style={{ fontSize: 20, marginBottom: 2 }}>{icon}</div>{label}
      </button>
    );
    return (
      <div style={{ padding: "12px 12px 24px", background: "#1a1a1a", minHeight: "100%", color: "#ddd", fontFamily: "inherit" }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", marginBottom: 2, letterSpacing: "-0.01em" }}>
          {nombre ? `Hola, ${nombre}` : "Binimeliz hoy"}
        </div>
        <div style={{ fontSize: 11, color: "#8a8a8a", marginBottom: 14, textTransform: "capitalize" }}>
          {new Date().toLocaleDateString("es-CL", { weekday: "long", day: "numeric", month: "long" })}
        </div>

        {/* Pulso del negocio — solo admin/accountant (se auto-oculta si no) */}
        <PulseNegocio isMobile={isMobile} />

        {/* Cola contable — la cola de trabajo del accountant (admin también la ve).
            Fetch independiente por tarjeta; se auto-oculta para otros roles. */}
        <PulsoContable isMobile={isMobile} />

        {/* Pulso del taller — espejo operativo para operator (admin también lo ve).
            Se calcula desde las mismas OTs ya cargadas arriba; sin fetch extra. */}
        <PulsoTaller workOrders={workOrders} loading={loading} isMobile={isMobile} />

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {fm && (
            <CardNav icon="💰" titulo={`Facturado ${mesNombre}`}
              to={puedeVerPulsoContable ? "/contabilidad" : "/produccion"}
              valor={fm.cantidad_facturas > 0 ? fmtMoney(fm.total_monto) : null} color="#2ecc71"
              sub={fm.cantidad_facturas > 0 ? `neto s/IVA · ${fm.cantidad_facturas} facturas${fm.vs_mes_anterior_pct != null ? ` · ${fm.vs_mes_anterior_pct > 0 ? "↑" : "↓"}${Math.abs(fm.vs_mes_anterior_pct)}% vs mes anterior` : ""}${fm.top_cliente?.nombre ? ` · top: ${fm.top_cliente.nombre.split(" ")[0]}` : ""}` : "No hay facturas para calcular el período"} />
          )}
          {oca && (
            <CardNav icon="🔴" titulo="Por facturar" to="/produccion" urgente={(oca.monto_total || 0) > 0}
              valor={oca.total != null ? fmtMoney(oca.monto_total || 0) : null} color="#e74c3c"
              sub={oca.total != null ? `${oca.total} OCs activas sin facturar` : "No se recibió información de OCs"} />
          )}
          {reg && (reg.guias_count || 0) > 0 && (
            <CardNav icon="📦" titulo="Por regularizar" to="/produccion" urgente
              valor={`${reg.guias_count} guía${reg.guias_count === 1 ? "" : "s"}`} color="#a78bfa"
              sub={`${fmtMoney(reg.monto_total || 0)} entregado con guía, sin factura`} />
          )}
          <CardNav icon="🏭" titulo="Taller ahora" to="/produccion"
            valor={`${activas} OTs activas`} color="#3498db"
            sub={`${inProcess} fabricando · ${done} listas · ${cotizadas} cotizadas`} />
        </div>

        <div style={{ fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".06em", margin: "16px 0 8px" }}>Accesos rápidos</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* Auditoría 28-ago: accesos según rol — el contable puro no opera
              Bodega/Trabajos y Cuadrilla es exclusiva del admin (403 si no). */}
          <Acceso icon="🏭" label="Producción" to="/produccion" />
          {!esContablePuro && <Acceso icon="📦" label="Bodega" to="/inventory" />}
          {!esContablePuro && <Acceso icon="🔧" label="Trabajos" to="/workOrders" />}
          {puedeVerCuadrilla && <Acceso icon="🤖" label="Cuadrilla" to="/cuadrilla" />}
          {puedeVerCuadrilla && <Acceso icon="📚" label="Biblioteca" to="/biblioteca" />}
        </div>

        <div style={{ fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".06em", margin: "18px 0 8px" }}>Últimas OTs</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {loading && <div style={{ color: "#8a8a8a", fontSize: 12 }}>Cargando…</div>}
          {recentWO.slice(0, 6).map(wo => (
            <div key={wo._id} onClick={() => navigate("/workOrders")}
              className="t-02 tap-card"
              style={{ display: "flex", alignItems: "center", gap: 8, background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: "11px 12px", cursor: "pointer" }}>
              <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#f39c12", flexShrink: 0, fontSize: 13 }}>OT {wo.number}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13, color: "#b8b8b8" }}>
                {wo.client?.name || wo.name || "—"}
              </span>
              <Badge map={WO_STATUS} status={wo.status} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: "block",
      padding: "14px 20px", background: "#1a1a1a", minHeight: "100%",
      color: "#ddd", fontFamily: "Arial, sans-serif", fontSize: 13,
      textAlign: "left", boxSizing: "border-box", width: "100%",
      overflowY: "auto",
    }}>

      {/* ── Top row ── */}
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
        <div>
          {/* 28-ago — saludo con nombre + fecha, como la rama móvil */}
          <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", letterSpacing: "-0.01em" }}>
            {nombre ? `Hola, ${nombre}` : "Binimeliz hoy"}
          </div>
          <div style={{ fontSize: 11, color: "#8a8a8a", marginTop: 2, textTransform: "capitalize" }}>
            {new Date().toLocaleDateString("es-CL", {
              weekday: "long", day: "numeric", month: "long", year: "numeric",
            })}
            <span style={{ textTransform: "none" }}> · Binimeliz S.A.</span>
          </div>
        </div>
        {/* Auditoría 28-ago: crear OT y programar Gantt son acciones de
            admin/operator — seller/accountant no las ven (el backend igual
            las rechaza). */}
        {puedeVerTaller && (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            onClick={() => navigate("/home")}
            style={{
              background: "transparent", color: "#cbd5e1", border: "1px solid #444",
              borderRadius: 6, padding: "7px 14px", fontSize: 12, cursor: "pointer",
            }}
          >
            <i className="bi bi-bar-chart-gantt" style={{ marginRight: 5 }} />
            Carta Gantt
          </button>
          <button
            onClick={() => navigate("/workOrders/new")}
            style={{
              background: "#e30613", color: "#fff", border: "none",
              borderRadius: 6, padding: "7px 16px", fontSize: 12,
              fontWeight: 700, cursor: "pointer",
            }}
          >
            + Nueva OT
          </button>
        </div>
        )}
      </div>

      {/* Pulso del negocio — tablero ejecutivo, solo admin/accountant.
          Carga independiente: no espera al {loading} de las OTs/OCs. */}
      <PulseNegocio isMobile={isMobile} />

      {/* Cola contable — tareas pendientes del contable (accountant/admin);
          fetch independiente por tarjeta, se auto-oculta para otros roles. */}
      <PulsoContable isMobile={isMobile} />

      {/* Pulso del taller — espejo operativo para operator (admin también lo ve).
          Donde el admin ve el pulso financiero, el taller ve SUS números:
          en proceso, listas para entrega, trancadas 5+ días y entregas de la
          semana. Calculado de las OTs ya cargadas — cero backend nuevo. */}
      <PulsoTaller workOrders={workOrders} loading={loading} isMobile={isMobile} />

      <>
          {/* ── 4 métricas en fila (cliqueables: filtran el Registro general) ── */}
          <div style={{ display: "flex", flexDirection: "row", gap: 8, marginBottom: 14, flexWrap: isMobile ? "wrap" : "nowrap" }}>
            {/* F5.8b — métricas operativas, no históricas. "Total OTs" antes mezclaba todas. */}
            {/* En móvil las 4 tarjetas quedan 2×2 (minWidth 42%) en vez de 4 apretadas en fila */}
            <MetricCard label="OTs activas"     value={activas}    sub={`de ${total} totales`}    color="blue"   style={isMobile ? { minWidth: "42%" } : null}
              loading={loading} active={regStatusFilter === "activas"}
              onClick={() => setRegStatusFilter(p => p === "activas" ? "" : "activas")} />
            <MetricCard label="En proceso"       value={inProcess}  sub="activas ahora"           color="amber"  style={isMobile ? { minWidth: "42%" } : null}
              loading={loading} active={regStatusFilter === "inProcess"}
              onClick={() => setRegStatusFilter(p => p === "inProcess" ? "" : "inProcess")} />
            <MetricCard label="Terminadas"       value={done}       sub="listas para entrega"     color="green"  style={isMobile ? { minWidth: "42%" } : null}
              loading={loading} active={regStatusFilter === "done"}
              onClick={() => setRegStatusFilter(p => p === "done" ? "" : "done")} />
            <MetricCard label="Facturadas"       value={facturadas} sub={`+ ${entregadas} con guía`} color="purple" style={isMobile ? { minWidth: "42%" } : null}
              loading={loading} active={regStatusFilter === "facturadas"}
              onClick={() => setRegStatusFilter(p => p === "facturadas" ? "" : "facturadas")} />
          </div>

          {/* ── Registro General del Taller ── */}
          <div style={{ display:"flex", flexDirection:"row", alignItems:"center", justifyContent:"space-between", marginBottom:8, flexWrap:"wrap", gap:6 }}>
            <div style={{ fontSize:10, fontWeight:700, color: "#999", textTransform:"uppercase", letterSpacing:".07em" }}>
              Registro general del taller
            </div>
            <div style={{ display:"flex", flexDirection:"row", gap:6, flexWrap:"wrap", width: isMobile ? "100%" : undefined }}>
              <input
                placeholder="Buscar OT, cliente, descripción..."
                value={regFilter}
                onChange={e => setRegFilter(e.target.value)}
                style={{
                  background:"#161616", border:"1px solid #333", borderRadius:5,
                  padding:"5px 10px", fontSize:11, color:"#ddd",
                  fontFamily:"Arial,sans-serif", width: isMobile ? "100%" : 220,
                  boxSizing: "border-box", flex: isMobile ? "1 1 100%" : undefined,
                }}
              />
              <select
                value={METRIC_FILTERS[regStatusFilter] ? "" : regStatusFilter}
                onChange={e => setRegStatusFilter(e.target.value)}
                style={{
                  background:"#161616", border:"1px solid #333", borderRadius:5,
                  padding:"5px 8px", fontSize:11, color:"#ddd",
                  fontFamily:"Arial,sans-serif",
                }}
              >
                <option value="">Todos los estados</option>
                <option value="pending">Sin tomar</option>
                <option value="approved">Aprobada (COT)</option>
                <option value="working">En proceso</option>
                <option value="ready">Terminado</option>
                <option value="delivered">Entregado</option>
              </select>
              {/* Chips de filtros activos (métrica / origen) con quitar al click */}
              {METRIC_FILTERS[regStatusFilter] && (
                <button
                  onClick={() => setRegStatusFilter("")}
                  className="t-02"
                  style={{
                    background: "#3a1114", color: "#ff8a8f", border: "1px solid #e30613",
                    borderRadius: 6, padding: "4px 10px", fontSize: 10, fontWeight: 700,
                    cursor: "pointer", whiteSpace: "nowrap",
                  }}
                  title="Quitar el filtro de la tarjeta"
                >
                  {{ activas: "OTs activas", inProcess: "En proceso", done: "Terminadas", facturadas: "Facturadas" }[regStatusFilter]} ✕
                </button>
              )}
              {regOriginFilter && (
                <button
                  onClick={() => setRegOriginFilter("")}
                  className="t-02"
                  style={{
                    background: `${ORIGIN_PILL[regOriginFilter].color}22`,
                    color: ORIGIN_PILL[regOriginFilter].color,
                    border: `1px solid ${ORIGIN_PILL[regOriginFilter].color}`,
                    borderRadius: 6, padding: "4px 10px", fontSize: 10, fontWeight: 700,
                    cursor: "pointer", whiteSpace: "nowrap",
                  }}
                  title="Quitar el filtro por origen"
                >
                  Origen: {ORIGIN_PILL[regOriginFilter].label} ✕
                </button>
              )}
              <div style={{ fontSize:11, color: "#999", alignSelf:"center", whiteSpace:"nowrap" }}>
                {regFiltered.length} de {workOrders.length}
              </div>
            </div>
          </div>

          {/* En móvil la tabla mantiene su ancho real y se desplaza lateral (swipe); en desktop igual que siempre */}
          <div style={{ border:"1px solid #2e2e2e", borderRadius:8, overflow:"hidden", background:"#242424", marginBottom:18, height:"calc(100vh - 290px)", overflowY:"auto", overflowX: isMobile ? "auto" : "hidden", WebkitOverflowScrolling: "touch" }}>
            <table style={{ width:"100%", minWidth: isMobile ? 900 : undefined, borderCollapse:"collapse", fontSize:11, tableLayout:"fixed" }}>
              <colgroup>
                <col style={{ width:"90px"  }} />
                <col style={{ width:"80px"  }} />
                <col style={{ width:"200px" }} />
                <col />
                <col style={{ width:"110px" }} />
                <col style={{ width:"55px"  }} />
                <col style={{ width:"85px"  }} />
                <col style={{ width:"120px" }} />
                <col style={{ width:"80px"  }} />
                <col style={{ width:"80px"  }} />
              </colgroup>
              <thead style={{ position:"sticky", top:0, zIndex:2 }}>
                <tr>
                  <th style={{ ...S.th }}>Fecha</th>
                  <th style={{ ...S.th }}>N° OT</th>
                  <th style={{ ...S.th }}>Cliente</th>
                  <th style={{ ...S.th }}>Descripción</th>
                  <th style={{ ...S.th, textAlign:"right" }}>P. Total</th>
                  <th style={{ ...S.th, textAlign:"center" }}>C/S OC</th>
                  <th style={{ ...S.th }}>N° OC</th>
                  <th style={{ ...S.th }}>Estado</th>
                  <th style={{ ...S.th }}>N° Guía</th>
                  <th style={{ ...S.th }}>N° Fact.</th>
                </tr>
              </thead>
                <tbody>
                  {loading ? (
                    /* 28-ago — skeleton de filas grises mientras cargan las OTs */
                    Array.from({ length: 8 }).map((_, i) => (
                      <tr key={`skeleton-${i}`}>
                        {Array.from({ length: 10 }).map((__, j) => (
                          <td key={j} style={{ ...S.td, padding: "10px" }}>
                            <div style={{
                              height: 10, background: "#2e2e2e", borderRadius: 4,
                              opacity: 0.55, width: `${55 + ((i + j) % 4) * 12}%`,
                            }} />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : regFiltered.length === 0 ? (
                    <tr>
                      <td colSpan={10} style={{ ...S.td, textAlign:"center", color: "#999", padding:"24px 0" }}>
                        Sin registros
                      </td>
                    </tr>
                  ) : (
                    regFiltered.map((wo, idx) => {
                      const sinFactura = (wo.status === "ready" || wo.status === "delivered") && !wo.factura;
                      const isRecent   = idx < 10 && !regFilter && !regStatusFilter && !regOriginFilter;
                      const rowBg      = isRecent ? "#262626" : idx % 2 === 0 ? "#242424" : "#222";
                      return (
                        <tr
                          key={wo._id}
                          style={{ cursor:"pointer", background: rowBg, borderLeft: isRecent ? "2px solid #3498db" : "2px solid transparent" }}
                          onMouseEnter={e => Array.from(e.currentTarget.cells).forEach(c => c.style.background="#2a2a2a")}
                          onMouseLeave={e => Array.from(e.currentTarget.cells).forEach(c => c.style.background="")}
                        >
                          <td style={S.td}>{fmt(wo.createdAt)}</td>
                          <td style={{ ...S.td, fontFamily:"monospace", color:"#3498db", fontWeight:700 }}>
                            {wo.number || "—"}
                          </td>
                          <td style={S.td}>{wo.client?.name || "—"}</td>
                          <td style={{ ...S.td, color: isRecent ? "#fff" : "#bbb" }}>{wo.name || "—"}</td>
                          <td style={{ ...S.td, textAlign:"right", color:"#2ecc71", fontWeight:600 }}>{fmtMoney(wo.total)}</td>
                          <td style={{ ...S.td, overflow:"visible", textAlign:"center" }}>
                            {/* 28-ago — pill cliqueable: filtra por origen (toggle) */}
                            {(() => {
                              const ok = originKey(wo);
                              const p = ORIGIN_PILL[ok];
                              const activo = regOriginFilter === ok;
                              return (
                                <span
                                  onClick={e => {
                                    e.stopPropagation();
                                    setRegOriginFilter(prev => prev === ok ? "" : ok);
                                  }}
                                  title={activo ? "Quitar filtro por origen" : `Filtrar por origen: ${p.label}`}
                                  style={{
                                    display:"inline-block", fontSize:9, fontWeight:700,
                                    padding:"1px 5px", borderRadius:20, cursor:"pointer",
                                    background:p.bg, color:p.color,
                                    border:`1px solid ${activo ? p.color : p.border}`,
                                  }}
                                >{p.label}</span>
                              );
                            })()}
                          </td>
                          <td style={{ ...S.td, fontFamily:"monospace", color: "#bbb" }}>{wo.clientOC || ""}</td>
                          <td style={{ ...S.td, overflow:"visible" }}>
                            <Badge map={WO_STATUS} status={wo.status} />
                          </td>
                          <td style={{ ...S.td, fontFamily:"monospace", color: "#bbb" }}>{wo.guiaDespacho || ""}</td>
                          <td style={{ ...S.td, fontFamily:"monospace", color: sinFactura ? "#e74c3c" : "#777" }}>
                            {wo.factura || (sinFactura ? "⚠ pend." : "")}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
          </div>
      </>
    </div>
  );
};

export default Dashboard;
